import { useEffect, useState } from "react";
import { Instagram, Play } from "lucide-react";
import { useContent } from "../content-context";
import { Eyebrow, Reveal } from "./Reveal";

import API from "../api";

export default function FollowIsland() {
  const { social, instagramTeasers } = useContent();
  const [posts, setPosts] = useState(null);

  useEffect(() => {
    let alive = true;
    fetch(`${API}/api/instagram/posts`)
      .then((r) => r.json())
      .then((data) => {
        if (alive) setPosts(data && data.posts && data.posts.length ? data.posts : []);
      })
      .catch(() => {
        if (alive) setPosts([]);
      });
    return () => {
      alive = false;
    };
  }, []);

  const live = posts && posts.length > 0;
  const tiles = live ? posts.slice(0, 6) : instagramTeasers;

  return (
    <section data-testid="instagram-section" className="border-t border-gold/15 bg-navy-2 py-24 md:py-32">
      <div className="mx-auto max-w-[1440px] px-5 md:px-10">
        <div className="flex flex-col gap-8 md:flex-row md:items-end md:justify-between">
          <div className="max-w-2xl">
            <Reveal>
              <Eyebrow light>Life on the Island</Eyebrow>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-6 font-display text-4xl font-medium leading-tight text-ivory md:text-5xl" data-testid="instagram-heading">
                Follow our island adventures, unforgettable celebrations and breathtaking moments from Clew Bay
              </h2>
            </Reveal>
            <Reveal delay={0.18}>
              <p className="mt-5 max-w-xl text-sm font-light leading-relaxed text-ivory/80 md:text-base">
                {live
                  ? "The latest from Collanmore on Instagram — tap any post to see it on Instagram."
                  : "Wild Atlantic moments, island celebrations and days on the water — a glimpse of life on Collanmore."}
              </p>
            </Reveal>
          </div>
          <Reveal delay={0.24}>
            <a
              href={social.instagram}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="instagram-follow"
              className="inline-flex items-center gap-3 border border-gold px-8 py-4 text-xs font-semibold uppercase tracking-[0.22em] text-gold-2 transition-all duration-500 hover:bg-gold hover:text-navy"
            >
              <Instagram className="h-4 w-4" />
              Follow Us on Instagram
            </a>
          </Reveal>
        </div>

        <div className="mt-14 grid grid-cols-2 gap-4 md:grid-cols-3 lg:gap-5" data-testid="instagram-grid">
          {tiles.map((item, i) =>
            live ? (
              <Reveal key={item.id} delay={(i % 3) * 0.07}>
                <a
                  href={item.permalink}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={item.caption ? `Open on Instagram: ${item.caption.slice(0, 80)}` : "Open this post on Instagram"}
                  data-testid={`instagram-post-${i + 1}`}
                  className="group relative block aspect-square overflow-hidden"
                >
                  <img
                    src={item.preview_url}
                    alt={item.caption ? item.caption.slice(0, 100) : "Latest post from Collanmore Island Lodge on Instagram"}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-[1.6s] ease-out group-hover:scale-105"
                  />
                  {(item.product_type === "REELS" || item.media_type === "VIDEO") && (
                    <span className="absolute left-4 top-4 flex items-center gap-1.5 bg-navy/80 px-3 py-1 text-[10px] font-semibold uppercase tracking-widest2 text-gold-2">
                      <Play className="h-3 w-3" /> Reel
                    </span>
                  )}
                  <span className="absolute inset-0 flex items-center justify-center bg-navy/0 transition-colors duration-500 group-hover:bg-navy/55">
                    <Instagram className="h-7 w-7 text-ivory opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
                  </span>
                </a>
              </Reveal>
            ) : (
              <Reveal key={item.src} delay={(i % 3) * 0.07}>
                <a
                  href={social.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`Open Instagram — ${item.alt}`}
                  data-testid={`instagram-tile-${i + 1}`}
                  className="group relative block aspect-square overflow-hidden"
                >
                  <img src={item.src} alt={item.alt} loading="lazy" className="h-full w-full object-cover transition-transform duration-[1.6s] ease-out group-hover:scale-105" />
                  <span className="absolute inset-0 flex items-center justify-center bg-navy/0 transition-colors duration-500 group-hover:bg-navy/55">
                    <Instagram className="h-7 w-7 text-ivory opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
                  </span>
                </a>
              </Reveal>
            )
          )}
        </div>

        {!posts && (
          <p className="mt-10 text-center text-xs uppercase tracking-widest2 text-ivory/50" data-testid="instagram-loading">
            Loading the latest from Instagram…
          </p>
        )}
      </div>
    </section>
  );
}
